<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue"
import {
  DesktopComputerIcon,
  DocumentTextIcon,
  MapIcon,
  MicrophoneIcon,
} from "@heroicons/vue/outline"
import { FindAnAdvisorProfile } from "@/types/profile"
import {
  outboundCapture,
  useFindAnAdvisorProfile,
} from "@/composables/advisor-profile"
import AppLayout from "@/components/find-an-advisor/shared/AppLayout.vue"
import DisclosureAdvisor from "@/components/find-an-advisor/shared/DisclosureAdvisor.vue"
import GrayGridBG from "@/assets/gray-grid.webp"
import Prose from "@/components/shared/Prose.vue"
import ProfileHeader from "@/components/find-an-advisor/show/ProfileHeader.vue"
import ProfileListBlockCard from "@/components/find-an-advisor/show/ProfileListBlockCard.vue"
import InitialProps from "@/types/root"
import VideoEmbedInfo from "@/types/video-embed"
import Border from "@/components/shared/Border.vue"
import GradientBG from "@/assets/gradient-blueberry.webp"
import ProfileContactFormModal from "@/components/find-an-advisor/show/ProfileContactFormModal.vue"
import ProfileIconListItem from "@/components/find-an-advisor/show/ProfileIconListItem.vue"

const props = defineProps<{
  faaProfile: FindAnAdvisorProfile
  initialProps: InitialProps
  introVideoEmbedInfo: VideoEmbedInfo
}>()

const profile = ref({ ...props.faaProfile })

const {
  bioGraphs,
  feeOptionsList,
  idealClientsList,
  mediaMentionsList,
  publisherLogos,
  secRecordsList,
  socialsList,
} = useFindAnAdvisorProfile(profile)

const showFullBio = ref(false)
// NOTE: (spk) alternative to truncating bio, allow it to be full length and make the contact aside sticky
const bioRef = useTemplateRef("bio")
const bioOverflow = computed(() => {
  if (bioRef.value === null) {
    return false
  }

  return bioRef.value.offsetHeight - 150 > window.screen.height
})
const bioIsTruncated = computed(() => {
  if (bioOverflow.value === false) {
    return false
  }

  if (showFullBio.value === true) {
    return false
  }

  return true
})

const showContactForm = ref<boolean>(false)

const videoEmbedStyles =
  props.introVideoEmbedInfo.height === 0 ||
  props.introVideoEmbedInfo.width === 0 ||
  props.introVideoEmbedInfo.height > props.introVideoEmbedInfo.width
    ? "aspect-ratio: 16 / 9" // common 16x9 video aspect ratio
    : `aspect-ratio: ${props.introVideoEmbedInfo.width} / ${props.introVideoEmbedInfo.height}`

const linksList = computed(() => [
  {
    icon: DesktopComputerIcon,
    text: "Visit Website",
    url: profile.value.websiteUrl,
  },
  { icon: DocumentTextIcon, text: "Visit Blog", url: profile.value.blogUrl },
  {
    icon: MicrophoneIcon,
    text: "Visit Podcast",
    url: profile.value.podcastUrl,
  },
])
</script>

<template>
  <!--TODO: (spk) offer back to search link? - back button will currently hold last search query -->
  <AppLayout>
    <article>
      <!--Profile Header-->
      <ProfileHeader :profile="profile" />

      <div class="2xl:container">
        <div class="lg:flex lg:flex-wrap text-xy-black">
          <!--Contact  Info-->
          <div class="faa-block flex flex-col items-center lg:w-1/3 xl:w-[25%]">
            <div
              class="md:flex md:justify-between md:items-center md:gap-12 lg:block"
            >
              <div>
                <h3
                  class="text-xl leading-6 font-extrabold mb-2 text-neutral-600"
                >
                  Contact Information
                </h3>
                <Border size="short" class="mb-6" />
                <div
                  class="flex flex-col gap-3.5 text-sm leading-4 font-semibold"
                >
                  <div
                    v-if="profile.address"
                    class="inline-flex items-center gap-x-2"
                  >
                    <component
                      :is="MapIcon"
                      class="w-6 h-auto text-gray-500 shrink-0"
                    />
                    {{ profile.address }}
                  </div>

                  <div v-for="(link, idx) in linksList" :key="`link-${idx}`">
                    <div v-if="link.url">
                      <ProfileIconListItem
                        :icon="link.icon"
                        :url="link.url"
                        :text="link.text"
                        @click.prevent="outboundCapture(link.url)"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="socialsList.length > 0" class="mt-4">
                  <ul class="flex gap-2">
                    <li
                      v-for="social in socialsList"
                      :key="social.platform"
                      class="shrink-0 w-7"
                    >
                      <a
                        :href="social.url"
                        target="_blank"
                        @click.prevent="outboundCapture(social.url)"
                      >
                        <span class="sr-only">{{ social.platform }} link</span>
                        <component :is="social.icon" aria-hidden="true" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div
                class="mt-6 pt-6 border-t border-neutral-300 md:mt-0 md:pt-0 md:border-t-0 lg:mt-6 lg:pt-6 lg:border-t"
              >
                <button
                  class="xy-btn-lg w-full"
                  @click="showContactForm = true"
                >
                  Contact {{ profile.name }}
                </button>
              </div>
            </div>
          </div>

          <!--Clients and Fees-->
          <div class="relative faa-block lg:w-full lg:order-1 xl:w-[30%]">
            <div
              class="bg-scroll bg-center bg-repeat -z-0 absolute inset-0 xl:hidden"
              :style="`background-image: url(${GrayGridBG});`"
            />
            <div class="relative">
              <div
                class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1"
              >
                <ProfileListBlockCard
                  title="Ideal Clients"
                  :items="idealClientsList"
                />

                <ProfileListBlockCard
                  title="Fee Options"
                  :items="feeOptionsList"
                >
                  <p class="text-xs text-neutral-700">
                    *Fees are subject to change. Contact this advisor for their
                    most up to date fees.
                  </p>
                </ProfileListBlockCard>

                <ProfileListBlockCard
                  title="SEC Records"
                  :items="secRecordsList"
                />

                <ProfileListBlockCard
                  title="In The Media"
                  :items="mediaMentionsList"
                />
              </div>
            </div>
          </div>

          <!--Bio-->
          <div class="faa-block relative lg:w-2/3 xl:px-0 xl:w-[45%]">
            <div>
              <div class="mb-5">
                <h2
                  class="text-2xl leading-6 font-extrabold font-display lg:text-4xl lg:leading-9"
                >
                  About {{ profile.name }}
                </h2>
              </div>
              <!--NOTE: This content comes from oembed API and is trusted-->
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="introVideoEmbedInfo.html"
                :style="videoEmbedStyles"
                class="bg-black mb-5 overflow-hidden [&>*]:w-full [&>*]:h-full"
                v-html="introVideoEmbedInfo.html"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div>
                <!-- NOTE: (spk) max-h-screen could be problematic for the landscapers -->
                <div
                  :class="
                    bioIsTruncated ? ['max-h-screen', 'overflow-hidden'] : ''
                  "
                >
                  <div ref="bio">
                    <div>
                      <Prose class="text-xy-black 2xl:prose-lg">
                        <p v-for="(graph, index) in bioGraphs" :key="index">
                          {{ graph }}
                        </p>
                      </Prose>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="bioIsTruncated"
                class="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end h-1/4 bg-gradient-to-t from-white to-transparent"
              >
                <div class="mb-8">
                  <button
                    class="xy-btn-secondary-lg"
                    @click="showFullBio = true"
                  >
                    Show Full Bio
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>

    <!--Publisher Logos-->
    <div v-if="publisherLogos.length > 0" class="bg-neutral-50">
      <div class="faa-block max-w-7xl flex flex-col items-center">
        <h3 class="h1 font-display font-extrabold">Featured In</h3>
        <Border size="wide" class="my-5" />
        <div class="flex flex-wrap items-center justify-center w-full">
          <div
            v-for="image in publisherLogos"
            :key="image"
            class="shrink-0 w-1/2 sm:w-1/3 xl:w-1/5 px-5 py-7"
          >
            <!-- 
            NOTE:(spk) using svgs as an img src doesn't support features like fill-current.
            However, vector versions of some publisher logos could not be found, so embedding the
            raw svg markup isn't useful.  We'll use the invert filter to give them a subtle
            desaturated look which will work with any image types we end up with.
            -->
            <img class="max-h-8 xl:max-h-14 w-full invert-[.3]" :src="image" />
          </div>
        </div>
      </div>
    </div>

    <!--CTA-->
    <div class="bg-xy-blue px-10 pt-16 pb-14 text-white relative">
      <div
        class="bg-scroll bg-cover bg-left-top absolute inset-0 z-0"
        :style="`background-image: url(${GradientBG});`"
      />
      <div class="flex flex-col items-center relative z-10">
        <div
          class="max-w-lg lg:flex lg:items-center lg:justify-between lg:max-w-3xl"
        >
          <p
            class="text-3xl leading-8 font-extrabold font-display text-neutral-50 lg:text-4xl lg:leading-9"
          >
            Find Your Fee-Only Financial Advisor Today
          </p>
          <p class="mt-5 lg:mt-0 lg:shrink-0">
            <button
              class="faa-btn-outline-inverse"
              @click="showContactForm = true"
            >
              Contact {{ profile.name }}
            </button>
          </p>
        </div>
      </div>
    </div>

    <template #disclosure>
      <DisclosureAdvisor />
    </template>

    <!--Contact form-->
    <ProfileContactFormModal v-model="showContactForm" :profile="profile" />
  </AppLayout>
</template>
